import { useCallback, useEffect, useState } from "react";
import { fetchQuery, graphql } from "react-relay";
import { useSdkContext } from "scmp-sdk/components/contexts";
import type { NewsletterItem } from "scmp-sdk/components/newsletter/types";
import { useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";

import { useFallbackStorageValue } from "shared/hooks";
import { addMinutes, notEmpty } from "shared/lib/utils";

import type { hooksNewsletterQuery } from "scmp-app/queries/__generated__/hooksNewsletterQuery.graphql";

import { fetchUserContext, NewsletterSignUpAction, signUpNewsletters } from "./apis";
import { SignedUpNewsletterSwrFetchKey } from "./consts";
import { getLastSync, setLastSync } from "./helpers";
import { SignUpMatrixV1LocalStorageKey } from "./sign-up-newsletter/social-buttons/consts";

const query = graphql`
  query hooksNewsletterQuery($first: Int, $newsletterIds: [String]) {
    newsletters(first: $first, filter: { entityIds: $newsletterIds }) {
      edges {
        node {
          entityId
          name
          alternativeName
          description {
            text
          }
          summary {
            text
          }
          homepageDescription {
            text
          }
        }
      }
    }
  }
`;

const RECENTLY_SUBSCRIBED_KEY = "recently_subscribed_newsletters";
const RECENTLY_SUBSCRIBED_EXPIRY = 24 * 60 * 60 * 1000;

export const useNewsletterApi = () => {
  const { environment, user } = useSdkContext();
  if (!environment)
    console.error(
      "Relay environment not found, make sure you wrapped your application with RelayEnvironmentProvider",
    );

  const { remove: removeDataMatrixInLocalStorage, value: dataMatrixInLocalStorage } =
    useFallbackStorageValue<Record<string, unknown>>(SignUpMatrixV1LocalStorageKey, {
      defaultValue: {},
    });

  const { data, mutate: mutateSignedUpNewsletter } = useSWRImmutable(
    user ? SignedUpNewsletterSwrFetchKey : null,
    fetchUserContext(environment),
    {
      isPaused: () => Date.now() < addMinutes(getLastSync(), 60).getTime(),
      onSuccess: () => setLastSync(Date.now()),
      revalidateOnMount: true,
    },
  );

  const [isDataReady, setIsDataReady] = useState(false);
  const [isNewslettersDataReady, setIsNewslettersDataReady] = useState(false);

  useEffect(() => {
    if (data) setIsDataReady(true);
  }, [data]);

  const fetchNewsletterByIds = useCallback(
    async (newsletterIds: string[], first: number) => {
      try {
        const response = await fetchQuery<hooksNewsletterQuery>(environment, query, {
          first,
          newsletterIds,
        }).toPromise();

        const newsletters =
          response?.newsletters.edges.map<NewsletterItem>(({ node }) => ({
            alternativeName: node.alternativeName,
            description: node.description?.text ? { text: node.description.text } : undefined,
            entityId: node.entityId,
            homepageDescription: node.homepageDescription?.text
              ? { text: node.homepageDescription.text }
              : undefined,
            name: node.name,
            summary: node.summary?.text ? { text: node.summary.text } : undefined,
          })) ?? [];

        return newsletters;
      } finally {
        setIsNewslettersDataReady(true);
      }
    },
    [environment],
  );

  const { mutate } = useSWRConfig();

  const handleSubscribeNewsletter = useCallback(
    async (newsletterItemIds: string[], email: string, dataMatrix?: Record<string, unknown>) => {
      // If dataMatrix in context is empty, will use local storage one
      const isUseLocalStorageDataMatrix = !notEmpty(dataMatrix);
      const response = await signUpNewsletters({
        action: NewsletterSignUpAction.Subscribe,
        dataMatrix: isUseLocalStorageDataMatrix ? dataMatrixInLocalStorage : dataMatrix,
        email,
        environment,
        newsletterItemIds,
      });

      if (notEmpty(response.errors)) return response;

      setLastSync(0);
      await mutate(SignedUpNewsletterSwrFetchKey);

      // Clear the local storage if dataMatrix has been used
      if (isUseLocalStorageDataMatrix) removeDataMatrixInLocalStorage();
      return response;
    },
    [dataMatrixInLocalStorage, environment, mutate, removeDataMatrixInLocalStorage],
  );

  const handleUnsubscribeNewsletter = useCallback(
    async (newsletterItemIds: string[], email: string, dataMatrix?: Record<string, unknown>) => {
      const isUseLocalStorageDataMatrix = !notEmpty(dataMatrix);
      const response = await signUpNewsletters({
        action: NewsletterSignUpAction.Unsubscribe,
        dataMatrix: isUseLocalStorageDataMatrix ? dataMatrixInLocalStorage : dataMatrix,
        email,
        environment,
        newsletterItemIds,
      });

      if (notEmpty(response.errors)) return response;

      setLastSync(0);
      await mutate(SignedUpNewsletterSwrFetchKey);

      if (isUseLocalStorageDataMatrix) removeDataMatrixInLocalStorage();
      return response;
    },
    [dataMatrixInLocalStorage, environment, mutate, removeDataMatrixInLocalStorage],
  );

  return {
    data,
    fetchNewsletterByIds,
    handleSubscribeNewsletter,
    handleUnsubscribeNewsletter,
    isDataReady,
    isNewslettersDataReady,
    mutate: mutateSignedUpNewsletter,
  };
};

export const useSignedUpNewsletter = () => {
  const { data } = useNewsletterApi();
  const { user } = useSdkContext();
  const { value: recentlySubscribed } = useFallbackStorageValue<{
    items: string[];
    timestamp: number;
  }>(RECENTLY_SUBSCRIBED_KEY, {
    defaultValue: { items: [], timestamp: 0 },
  });

  const [signedUpNewsletterItemIds, setSignedUpNewsletterItemIds] = useState(data ?? []);

  // Preventing changes on transition by delaying the set of signed up newsletter
  useEffect(() => {
    // For logged-in users, use backend data
    if (user) {
      setSignedUpNewsletterItemIds(data ?? []);
      return;
    }

    // For anonymous users, use local storage data
    const now = Date.now();
    const recentIds =
      recentlySubscribed?.timestamp &&
      now - recentlySubscribed.timestamp <= RECENTLY_SUBSCRIBED_EXPIRY
        ? recentlySubscribed.items
        : [];

    setSignedUpNewsletterItemIds(recentIds);
  }, [data, user, recentlySubscribed]);

  return signedUpNewsletterItemIds;
};

export const useRecentlySubscribedNewsletters = () => {
  const { set: setRecentlySubscribed, value: recentlySubscribed } = useFallbackStorageValue<{
    items: string[];
    timestamp: number;
  }>(RECENTLY_SUBSCRIBED_KEY, {
    defaultValue: { items: [], timestamp: 0 },
  });

  const addRecentlySubscribed = useCallback(
    (newsletterIds: string[]) => {
      const now = Date.now();
      setRecentlySubscribed({
        items: [...new Set([...(recentlySubscribed?.items ?? []), ...newsletterIds])],
        timestamp: now,
      });
    },
    [recentlySubscribed?.items, setRecentlySubscribed],
  );

  const isRecentlySubscribed = useCallback(
    (newsletterId: string) => {
      const now = Date.now();
      if (!recentlySubscribed || now - recentlySubscribed.timestamp > RECENTLY_SUBSCRIBED_EXPIRY) {
        setRecentlySubscribed({ items: [], timestamp: now });
        return false;
      }
      return recentlySubscribed.items.includes(newsletterId);
    },
    [recentlySubscribed, setRecentlySubscribed],
  );

  return {
    addRecentlySubscribed,
    isRecentlySubscribed,
  };
};
